import type { Product, ProductVendor } from "@models/product";
import { MAX_LINE_QUANTITY, type ShoppingCartItem } from "@models/shoppingCart";
import { vendorRowsForSku } from "@utils/vendorRows";
import { lineHasNoOffers } from "./lineOffers";

export interface LineStock {
    // What the line's own vendor offering can supply.
    available: number;
    // Set only when the line is above `available`: the cheapest other offering of the
    // same sku that can take the whole line (#443 / C-07 — offer a vendor with enough
    // stock rather than refuse outright).
    alternative?: ProductVendor;
}

// The rows of THIS line's sku only (#390): a variant line's from its variant, a base line's
// from the product — never another sku's, which the line can't be moved to.
const skuRows = (item: ShoppingCartItem, product: Product): ProductVendor[] => {
    const variantId = item.variant?.variantId ?? null;
    // `?? []`: a product payload with no variants list reaches here (useCartProducts takes
    // any object as a product), and must leave the line with no stock, not throw.
    const variants = product.variants ?? [];

    return vendorRowsForSku(
        variantId === null
            ? product.vendors
            : variants.find((v) => v.id === variantId)?.vendors,
        variantId,
    );
};

// The cheapest of the other active, in-stock offerings of the line's sku whose stock covers
// the line a switch would leave there, and for which `fits` holds of that merged quantity. A
// switch merges into the line the cart already holds for that offering, and the API checks
// the merged quantity against that vendor's stock — and against the per-line limit (PR 339),
// whatever the stock.
const cheapestTarget = (
    item: ShoppingCartItem,
    product: Product,
    items: ShoppingCartItem[],
    fits: (merged: number) => boolean,
): ProductVendor | undefined => {
    let target: ProductVendor | undefined;

    for (const row of skuRows(item, product)) {
        if (row.id === item.vendorSkuId || !row.isActive || !row.inStock) {
            continue;
        }

        const merged =
            item.quantity +
            (items.find(
                (other) => other.id !== item.id && other.vendorSkuId === row.id,
            )?.quantity ?? 0);

        if ((row.availableQuantity ?? 0) < merged || !fits(merged)) {
            continue;
        }

        if (!target || row.price < target.price) {
            target = row;
        }
    }

    return target;
};

/**
 * The cheapest other active, in-stock offering of the line's sku that can take the whole
 * line, or undefined. Used for a line above its vendor's stock, and for one whose vendor no
 * longer offers it (offering_unavailable, #443).
 */
export const alternativeVendor = (
    item: ShoppingCartItem,
    product: Product | undefined,
    items: ShoppingCartItem[],
): ProductVendor | undefined =>
    product
        ? cheapestTarget(item, product, items, (merged) => merged <= MAX_LINE_QUANTITY)
        : undefined;

/** A vendor out of reach only because the merged line would pass the per-line limit. */
export interface LimitedAlternative {
    vendor: ProductVendor;
    // What the cart's line of that vendor must come down to for the switch to fit: the
    // per-line limit less this line. 0 or less means that line would have to go entirely.
    lowerTo: number;
}

/**
 * The cheapest other offering whose stock covers the merged line but which the per-line
 * limit (9,999) alone rules out (PR 345 review). A line its vendor no longer offers can't be
 * lowered itself — the API refuses every quantity change on it — but the line it would merge
 * into can.
 */
export const limitedAlternative = (
    item: ShoppingCartItem,
    product: Product | undefined,
    items: ShoppingCartItem[],
): LimitedAlternative | undefined => {
    const vendor = product
        ? cheapestTarget(item, product, items, (merged) => merged > MAX_LINE_QUANTITY)
        : undefined;

    return vendor && { vendor, lowerTo: MAX_LINE_QUANTITY - item.quantity };
};

/**
 * A cart line's vendor stock, read from GET /products/{id} — the cart payload carries
 * none. undefined when the product, the line's own row or its stock is unknown: the cart
 * then shows no limit, and the API still refuses a line above stock at checkout.
 */
export const lineStock = (
    item: ShoppingCartItem,
    product: Product | undefined,
    items: ShoppingCartItem[],
): LineStock | undefined => {
    if (!product) {
        return undefined;
    }

    const available = skuRows(item, product).find(
        (row) => row.id === item.vendorSkuId,
    )?.availableQuantity;

    if (available === undefined) {
        return undefined;
    }

    if (item.quantity <= available) {
        return { available };
    }

    const alternative = alternativeVendor(item, product, items);

    return alternative ? { available, alternative } : { available };
};

/**
 * Every line's stock, and how many lines are above it — the one derivation the Cart and
 * Checkout pages both close their way to an order on (#443 / C-07: checkout refuses the
 * whole order with a 412 while a line is above its vendor's stock).
 *
 * `overrides` (cart item id → stock) are stocks newer than the product reads — Checkout's
 * stock 412 states each line's stock as checkout read it — and win for their lines.
 *
 * A line no live vendor offers (PO ruling 2026-11-02) has no stock, override or not: it is
 * out of stock, not above it, and a dead offer's count limits nothing.
 */
export const cartStock = (
    items: ShoppingCartItem[],
    products: Map<string, Product>,
    overrides?: Map<string, number>,
): { stocks: (LineStock | undefined)[]; overStockCount: number } => {
    const stocks = items.map((item) => {
        if (lineHasNoOffers(item, products.get(item.productId))) {
            return undefined;
        }

        const override = overrides?.get(item.id);

        return override !== undefined
            ? { available: override }
            : lineStock(item, products.get(item.productId), items);
    });
    const overStockCount = items.filter((item, index) => {
        const stock = stocks[index];
        return stock !== undefined && item.quantity > stock.available;
    }).length;

    return { stocks, overStockCount };
};
